import { motion } from "framer-motion";
import { ArrowRight, Shield, Zap, Globe, CheckCircle, Users, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import NationalitySelect from "../components/NationalitySelect";
import { getNationalityCurrency } from "../data/nationalities";
import { useCurrency } from "../context/CurrencyContext";
import { useSEO, addJsonLd } from "../hooks/useSEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Globe3D() {
  return (
    <div className="relative w-72 h-72 md:w-80 md:h-80">
      {/* Globe */}
      <div className="absolute inset-8 rounded-full border-2 border-blue-200/40 animate-spin-slow" style={{ perspective: "600px" }}>
        <div className="absolute inset-0 rounded-full border border-dashed border-blue-300/30" style={{ transform: "rotateX(60deg)" }} />
        <div className="absolute inset-0 rounded-full border border-dashed border-blue-300/20" style={{ transform: "rotateX(60deg) rotateZ(60deg)" }} />
        <div className="absolute inset-0 rounded-full border border-dashed border-blue-300/20" style={{ transform: "rotateX(60deg) rotateZ(120deg)" }} />
        <div className="absolute inset-2 rounded-full bg-gradient-to-br from-blue-50 via-blue-100/50 to-indigo-50 opacity-40" />
      </div>

      {/* Orbiting airplane */}
      <div className="absolute inset-0 animate-orbit">
        <div className="w-8 h-8 bg-white rounded-lg shadow-lg shadow-blue-200/50 flex items-center justify-center border border-blue-100">
          <svg viewBox="0 0 24 24" className="w-4 h-4 text-blue-600" fill="currentColor"><path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/></svg>
        </div>
      </div>

      {/* Flight path SVG */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 320 320">
        <path d="M60 200 Q160 60 260 180" stroke="url(#pathGrad)" strokeWidth="1.5" fill="none" strokeDasharray="8 6" className="animate-dash" opacity="0.4"/>
        <path d="M40 140 Q180 280 280 120" stroke="url(#pathGrad)" strokeWidth="1" fill="none" strokeDasharray="6 4" className="animate-dash" opacity="0.25" style={{animationDelay:"1s"}}/>
        <defs><linearGradient id="pathGrad" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="#3B82F6" stopOpacity="0.6"/><stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.2"/></linearGradient></defs>
      </svg>

      {/* Floating country pins */}
      {[
        { top: "15%", left: "20%", flag: "\u{1F1FA}\u{1F1F8}", delay: 0 },
        { top: "60%", left: "75%", flag: "\u{1F1EC}\u{1F1E7}", delay: 0.5 },
        { top: "75%", left: "25%", flag: "\u{1F1E6}\u{1F1EA}", delay: 1 },
        { top: "25%", left: "70%", flag: "\u{1F1EF}\u{1F1F5}", delay: 1.5 },
      ].map((pin, i) => (
        <motion.div key={i} animate={{ y: [0, -6, 0] }} transition={{ repeat: Infinity, duration: 3, delay: pin.delay }}
          className="absolute w-9 h-9 bg-white rounded-xl shadow-md shadow-blue-100/50 flex items-center justify-center text-sm border border-blue-50"
          style={{ top: pin.top, left: pin.left }}>
          {pin.flag}
        </motion.div>
      ))}
    </div>
  );
}

export default function HeroSection() {
  const navigate = useNavigate();
  const scrollTo = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  const [countries, setCountries] = useState([]);
  const [destination, setDestination] = useState("");
  const [nationality, setNationality] = useState("");
  const { setCurrency } = useCurrency();
  const [recentCount] = useState(Math.floor(Math.random() * 8) + 9);

  useEffect(() => { axios.get(`${API}/countries`).then(r => setCountries(r.data)).catch(() => {}); }, []);

  useSEO({ title: "FlyInVisa - Apply Visa to 120+ Countries | Fast Processing, 98% Approval", description: "Apply visa online for 120+ countries. Check requirements, pricing, and documents. Transparent fees, real-time tracking. Trusted by 50K+ travelers.", keywords: ["visa application online", "apply visa", "e-visa", "visa processing", "travel visa", "visa from India", "UAE visa", "Schengen visa", "US visa"] });
  useEffect(() => { addJsonLd({ "@context": "https://schema.org", "@type": "Organization", "name": "FlyInVisa", "url": window.location.origin, "description": "Online visa processing platform for 120+ countries", "sameAs": [], "contactPoint": { "@type": "ContactPoint", "contactType": "customer service", "availableLanguage": ["English", "Hindi"] } }); }, []);

  return (
    <section id="hero" data-testid="hero-section" className="hero-gradient pt-28 pb-20 px-6">
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-6">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-xs font-semibold text-blue-600 tracking-wide">Trusted by 10,000+ travelers worldwide</span>
            </div>

            <h1 data-testid="hero-headline" className="heading-font text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-slate-900 mb-5">
              Apply Visa to{" "}
              <span className="text-gradient-blue">120+ Countries</span>{" "}
              Easily
            </h1>

            <p data-testid="hero-subheadline" className="text-base md:text-lg text-slate-500 max-w-lg mb-8 leading-relaxed">
              Check requirements, pricing, and apply in minutes. Transparent fees, real-time tracking, 98% approval rate.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => scrollTo("#visa-search")}
                data-testid="hero-browse-btn" className="btn-premium text-white font-semibold px-7 py-3.5 rounded-xl flex items-center justify-center gap-2">
                Browse Countries <ArrowRight className="w-4 h-4" />
              </motion.button>
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => navigate("/compare")}
                data-testid="hero-compare-btn" className="text-slate-700 font-semibold px-7 py-3.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-all shadow-sm">
                Compare Visas
              </motion.button>
            </div>

            {/* Quick Search Form */}
            <div className="glass-card p-4 mb-6 max-w-lg" data-testid="hero-quick-search">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-[10px] text-slate-500 mb-1 block font-medium">Destination</label>
                  <Select value={destination} onValueChange={setDestination}>
                    <SelectTrigger className="h-10 border-slate-200 rounded-lg text-sm"><SelectValue placeholder="Where to?" /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 max-h-48">
                      {countries.slice(0, 30).map(c => <SelectItem key={c.country_code} value={c.country_code}><span className="flex items-center gap-1.5 text-xs">{c.flag} {c.country_name}</span></SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 mb-1 block font-medium">Nationality</label>
                  <NationalitySelect value={nationality} onValueChange={v => { setNationality(v); setCurrency(getNationalityCurrency(v)); }} triggerClass="h-10 text-sm" />
                </div>
              </div>
              <button onClick={() => { if (destination && nationality) navigate(`/pricing?destination=${destination}&nationality=${nationality}`); }}
                disabled={!destination || !nationality} data-testid="hero-check-price"
                className="w-full h-10 btn-premium text-white text-sm font-semibold rounded-lg disabled:opacity-40 flex items-center justify-center gap-2">
                Check Price & Apply <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Real-time activity + urgency */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex items-center gap-1.5 text-xs text-slate-600 bg-amber-50 border border-amber-100 px-3 py-1.5 rounded-full">
                <Users className="w-3 h-3 text-amber-600" />
                <span><strong>{recentCount}</strong> people applied in last hour</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-1.5 rounded-full">
                <Shield className="w-3 h-3" /> Money-back guarantee
              </div>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center gap-4">
              {[
                { icon: Shield, label: "Secure & Encrypted", color: "text-emerald-600" },
                { icon: Zap, label: "50K+ Visas Processed", color: "text-blue-600" },
                { icon: CheckCircle, label: "4.9 Rating", color: "text-indigo-600" },
              ].map(t => (
                <div key={t.label} className="flex items-center gap-1.5 text-xs text-slate-500">
                  <t.icon className={`w-3.5 h-3.5 ${t.color}`} /> {t.label}
                </div>
              ))}
            </div>
          </motion.div>

          {/* 3D Globe */}
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden md:flex justify-center">
            <Globe3D />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
